import { RestartAlt } from '@mui/icons-material';
import { Button, Grid, MenuItem, Theme, Tooltip } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Form, FormikProvider, useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useContext, useEffect, useState } from 'react';
import * as Yup from 'yup';
import { Loader, OrderManagementPage, PageTitle, SMBSelect, Toast } from '../../../components';
import { UserContext } from '../../../context';
import {
  getOrderDetail,
  getOrders,
  getPlaceOrderChecklistDetails,
  getPlaceOrderHistory
} from '../../../fetch';
import { INeighborhood, IOrderDetail, IOrderHistory, IPart, ILot } from '../../../models';
import { OrderDetails } from './order-details';
import { PartsHistory } from './parts-history';
import { getCommonNeighborhoodLots, getProductionNeighborhoods } from '../../../fetch/lookups';

const PlaceOrderSchema = Yup.object().shape({
  neighborhoodId: Yup.string(),
  lotNumber: Yup.string(),
  chargeVendors: Yup.mixed().when('orderType', {
    is: (val: number) => val === 6 || val === 10,
    then: Yup.array(
      Yup.object().shape({
        vendorId: Yup.number().required('Required'),
        amount: Yup.string().required('Required')
      })
    ),
    otherwise: Yup.array().notRequired()
  }),
  returnVendor: Yup.string().nullable(),
  orderType: Yup.string().required('Required'),
  descriptionId: Yup.string().required('Required'),
  notes: Yup.string().required('Required'),
  active: Yup.boolean(),
  changeOrder: Yup.boolean(),
  photoGuids: Yup.array(),
  photos: Yup.mixed().when('orderType', {
    is: (val: string) => {
      return val === '9';
    },
    then: Yup.array().required('Required'),
    otherwise: Yup.array().notRequired()
  })
});

export const PlaceOrder = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const params = new URLSearchParams(window.location.search);

  const [neighborhoods, setNeighborhoods] = useState<INeighborhood[]>(null);
  const [lots, setLots] = useState<ILot[]>(null);
  const [selectedNeighborhood, setSelectedNeighborhood] = useState<INeighborhood | null>(null);
  const [isLoadingNeighborhoods, setNeighborhoodsLoading] = useState<boolean>(true);
  const [isLoadingNeighborhoodLots, setNeighborhoodsLotsLoading] = useState<boolean>(false);
  const [isError, showError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [history, setHistory] = useState<IOrderHistory[] | null>(null);
  const [isLoadingHistory, setLoadingHistory] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const [perPage, setRowsPerPage] = useState<number>(10);
  const [recordCount, setRecordCount] = useState<number>(0);
  const [selectedOrderType, setSelectedOrderType] = useState<string | null>(null);
  const [isLoadingOrders, setLoadingOrders] = useState<boolean>(false);
  const [favoriteOrders, setFavoriteOrders] = useState<IOrderDetail[] | null>(null);
  const [selectedFavoriteOrder, setSelectedFavoriteOrder] = useState<string | number>('');
  const [isLoadingFavoriteOrder, setLoadingFavoriteOrder] = useState<boolean>(false);
  const [parts, setParts] = useState<IPart[]>([]);
  const [isLoadingChecklist, setIsLoadingChecklist] = useState<boolean>(false);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      neighborhoodId: '',
      lotNumber: '',
      chargeVendors: [],
      returnVendor: '',
      orderType: '',
      descriptionId: '',
      notes: '',
      active: true,
      changeOrder: false,
      photos: [],
      photoGuids: []
    },
    validationSchema: PlaceOrderSchema,
    onSubmit: async (values, actions) => {}
  });

  const {
    initialValues,
    values,
    setFieldValue,
    errors,
    touched,
    handleBlur,
    isValid,
    setFieldTouched
  } = formik;

  const fetchFavoriteOrders = async () => {
    try {
      setLoadingOrders(true);
      const res = await getOrders({
        isFavorite: true,
        sortBy: 'FavoriteName'
      });
      setFavoriteOrders(res.records);
    } catch (error) {
      enqueueSnackbar('Error loading orders, please refresh and try again.', {
        variant: 'error'
      });
      console.log(error);
    } finally {
      setLoadingOrders(false);
    }
  };

  const { isWarrantyAdmin, hasWarrantyRole } = useContext(UserContext);

  const fetchHistory = async (
    selectedPage?: number,
    selectedPerPage?: number,
    orderType?: string
  ) => {
    try {
      setLoadingHistory(true);
      const res = await getPlaceOrderHistory({
        neighborhoodId: selectedNeighborhood?.neighborhoodId,
        lotNumber: values.lotNumber,
        page: (selectedPage || 0) + 1,
        perPage: selectedPerPage || 10,
        sortDirection: 'Desc',
        orderType: orderType || selectedOrderType
      });
      setHistory(res.records);
      setRecordCount(res.totalRecordCount);
    } catch (error) {
      console.log(error);
    } finally {
      setLoadingHistory(false);
    }
  };
  useEffect(() => {
    if (values.lotNumber && selectedNeighborhood?.neighborhoodId) {
      fetchHistory(0, 10);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNeighborhood, values.lotNumber]);

  const fetchNeighborhoodLots = async () => {
    setNeighborhoodsLotsLoading(true);
    try {
      const res = await getCommonNeighborhoodLots({
        neighborhoodId: selectedNeighborhood?.neighborhoodId,
        includeGeneralJob: selectedNeighborhood?.hasGeneralJob
      });
      if (res) {
        setLots(res);
      } else {
        console.error(`No 'records' on lots response.`);
        setErrorMessage('Error loading lots, please try again.');
      }
    } catch (error) {
      console.error(error);
      setErrorMessage('Error loading lots, please try again.');
    }
    setNeighborhoodsLotsLoading(false);
  };

  const fetchNeighborhoods = async () => {
    try {
      const res = await getProductionNeighborhoods({
        isActive: isWarrantyAdmin || hasWarrantyRole ? null : true,
        hasActiveJobs: false,
        sortBy: 'Name'
      });
      if (res) {
        setNeighborhoods(res.records);
      } else {
        console.error(`No 'records' on neighborhoods response.`);
        setErrorMessage('Error loading neighborhoods, please try again.');
      }
    } catch (error) {
      console.error(error);
      setErrorMessage('Error loading neighborhoods, please try again.');
    }
    setNeighborhoodsLoading(false);
  };

  const fetchChecklistDetails = async () => {
    try {
      setIsLoadingChecklist(true);
      const res = await getPlaceOrderChecklistDetails(params?.get('purchaseOrderId'));
      if (res) {
        setFieldValue('neighborhoodId', res.neighborhoodId);
        const selectedNeighborhood = neighborhoods?.find(
          n => n.neighborhoodId === res.neighborhoodId
        );
        if (selectedNeighborhood) {
          setSelectedNeighborhood(selectedNeighborhood);
        }
        setFieldValue('lotNumber', res.lotNumber ?? '');
        // return po order type
        setFieldValue('orderType', 9);
        setFieldValue('descriptionId', res?.fieldOrderDescriptionId ?? '');
        setFieldValue('returnVendor', res?.sageVendorId ?? '');
      }
    } catch (e) {
    } finally {
      setIsLoadingChecklist(false);
    }
  };

  useEffect(() => {
    if (params?.get('purchaseOrderId') && !values.neighborhoodId && neighborhoods?.length > 0) {
      fetchChecklistDetails();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [neighborhoods]);

  useEffect(() => {
    fetchNeighborhoods();
    fetchFavoriteOrders();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedNeighborhood?.neighborhoodId) {
      fetchNeighborhoodLots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNeighborhood]);

  return (
    <>
      <FormikProvider value={formik}>
        <Form>
          <OrderManagementPage title="Place Order">
            {isLoadingChecklist && <Loader type="fullscreen" position="centered" />}
            <PageTitle
              title="Place Order"
              subTitle="Please select Neighborhood, Lot and optional Favorite Order to get started with your order."
            />
            <Grid container alignItems="center" spacing={1} className={classes.neighborhoodWrapper}>
              <Grid item xs={12} md={3}>
                <SMBSelect
                  variant="outlined"
                  onChange={e => {
                    setFieldValue('neighborhoodId', e.target.value);
                    setSelectedNeighborhood(
                      neighborhoods.find(n => n.neighborhoodId === Number(e.target.value))
                    );
                    setFieldValue('lotNumber', '');
                    setPage(0);
                    setRowsPerPage(10);
                    setFieldValue('notes', '');
                    setFieldValue('descriptionId', '');
                    setFieldValue('orderType', '');
                    setFieldValue('changeOrder', false);
                    setParts([]);
                    if (selectedOrderType) {
                      setSelectedOrderType(null);
                      fetchHistory();
                    }
                  }}
                  name={`neighborhoodId`}
                  label={isLoadingNeighborhoods ? 'Loading...' : 'Neighborhood'}
                  id={`neighborhoodId`}
                  handleClear={() => {
                    setFieldValue('neighborhoodId', '');
                    setSelectedNeighborhood(null);
                    setFieldValue('lotNumber', '');
                    setPage(0);
                    setRowsPerPage(10);
                    setFieldValue('notes', '');
                    setFieldValue('descriptionId', '');
                    setFieldValue('orderType', '');
                    setFieldValue('changeOrder', false);
                    setParts([]);
                    setSelectedFavoriteOrder('');
                    if (selectedOrderType) {
                      setSelectedOrderType(null);
                      fetchHistory();
                    }
                  }}
                  disabled={isLoadingNeighborhoods}
                  value={values.neighborhoodId}
                  onBlur={handleBlur}
                  error={errors && errors.neighborhoodId ? true : false}
                >
                  {!isLoadingNeighborhoods && neighborhoods && neighborhoods.length === 0 && (
                    <MenuItem disabled>No Neighborhoods available</MenuItem>
                  )}
                  {!isLoadingNeighborhoods &&
                    neighborhoods &&
                    neighborhoods.map((neighborhood: INeighborhood, index) => {
                      return (
                        <MenuItem key={`${index}`} value={neighborhood.neighborhoodId}>
                          {neighborhood.name} Phase {neighborhood.phase}
                        </MenuItem>
                      );
                    })}
                </SMBSelect>
              </Grid>
              <Grid item xs={12} md={3}>
                <SMBSelect
                  onChange={e => {
                    setFieldValue('lotNumber', e.target.value);
                    setPage(0);
                    setRowsPerPage(10);
                  }}
                  name={`lotNumber`}
                  label={isLoadingNeighborhoodLots ? 'Loading...' : 'Lot'}
                  id={`lotNumber`}
                  variant="outlined"
                  handleClear={() => {
                    setFieldValue('lotNumber', '');
                    setPage(0);
                    setRowsPerPage(10);
                    setSelectedFavoriteOrder('');
                  }}
                  disabled={isLoadingNeighborhoodLots || !selectedNeighborhood}
                  value={values.lotNumber}
                  onBlur={handleBlur}
                  error={errors && errors.lotNumber ? true : false}
                >
                  {!isLoadingNeighborhoodLots && lots?.length === 0 && (
                    <MenuItem disabled>No Lots available</MenuItem>
                  )}
                  {!isLoadingNeighborhoodLots &&
                    lots &&
                    lots.map((lot: ILot, index) => {
                      return (
                        <MenuItem key={`${index}`} value={lot.lotNumber}>
                          {lot.description}
                        </MenuItem>
                      );
                    })}
                </SMBSelect>
              </Grid>
              <Grid item xs={12} md={3}>
                <Tooltip
                  title="Please select neighborhood and lot first"
                  disableHoverListener={!!selectedNeighborhood && values.lotNumber ? true : false}
                >
                  <div>
                    <SMBSelect
                      variant="outlined"
                      label={isLoadingOrders ? 'Loading...' : 'Favorite Order'}
                      onChange={async e => {
                        setSelectedFavoriteOrder(e.target.value);
                        setLoadingFavoriteOrder(true);
                        try {
                          const selectedOrder = await getOrderDetail(e.target.value);
                          if (selectedOrder?.fieldOrderId) {
                            setFieldValue(
                              'descriptionId',
                              (selectedOrder?.descriptionId
                                ? `${selectedOrder?.descriptionId}`
                                : '') as string
                            );
                            setFieldValue('orderType', `${selectedOrder.type}`);
                            setFieldValue('changeOrder', selectedOrder.isChangeOrder ?? false);
                            setFieldValue('chargeVendors', selectedOrder.chargeVendors ?? []);
                            setParts(
                              selectedOrder.parts.map(p => ({
                                id: p.partId,
                                partName: p.partName,
                                quantity: p.quantity,
                                unit: p.unit
                              }))
                            );
                          }
                        } catch (error) {
                          console.log(error);
                        } finally {
                          setLoadingFavoriteOrder(false);
                        }
                      }}
                      size="small"
                      disabled={isLoadingOrders || !selectedNeighborhood || !values.lotNumber}
                      value={selectedFavoriteOrder}
                      handleClear={() => {
                        setPage(0);
                        setRowsPerPage(10);
                        setFieldValue('notes', '');
                        setFieldValue('descriptionId', '');
                        setFieldValue('orderType', '');
                        setFieldValue('changeOrder', false);
                        setParts([]);
                        if (selectedOrderType) {
                          setSelectedOrderType(null);
                          fetchHistory();
                        }
                        setSelectedFavoriteOrder('');
                      }}
                    >
                      {!isLoadingOrders && favoriteOrders?.length === 0 && (
                        <MenuItem disabled>No Favorite orders</MenuItem>
                      )}
                      {favoriteOrders?.map((order: IOrderDetail, index) => {
                        return (
                          <MenuItem key={`${index}`} value={order.fieldOrderId}>
                            {order.favoriteName}
                          </MenuItem>
                        );
                      })}
                    </SMBSelect>
                  </div>
                </Tooltip>
              </Grid>
              {selectedFavoriteOrder && (
                <Grid item width={{ xs: '100%', md: 'auto' }}>
                  <Button
                    startIcon={<RestartAlt />}
                    color="secondary"
                    variant="contained"
                    onClick={() => {
                      setSelectedFavoriteOrder('');
                      setFieldValue('notes', '');
                      setFieldValue('descriptionId', '');
                      setFieldValue('orderType', '');
                      setFieldValue('changeOrder', false);
                      setFieldValue('chargeVendors', []);
                      setParts([]);
                    }}
                  >
                    Reset Order
                  </Button>
                </Grid>
              )}
            </Grid>
            {selectedNeighborhood && values.lotNumber && (
              <Grid container spacing={1} sx={{ marginTop: '1rem' }}>
                <OrderDetails
                  values={values}
                  initialValues={initialValues}
                  setFieldValue={setFieldValue}
                  handleBlur={handleBlur}
                  errors={errors}
                  touched={touched}
                  parts={parts}
                  setParts={setParts}
                  setFieldTouched={setFieldTouched}
                  handleOrderTypeChange={val => {
                    setSelectedOrderType(val);
                    fetchHistory(null, null, val);
                  }}
                  isLoadingHistory={isLoadingHistory}
                  fetchHistory={(page, perPage) => {
                    setPage(page);
                    setRowsPerPage(perPage);
                    fetchHistory(page, perPage);
                  }}
                  isValid={isValid}
                  isLoadingFavoriteOrder={isLoadingFavoriteOrder}
                  isReturnPurchaseOrder={params?.get('purchaseOrderId') ? true : false}
                />
                <PartsHistory
                  history={history}
                  isLoadingHistory={isLoadingHistory}
                  page={page}
                  perPage={perPage}
                  setRowsPerPage={setRowsPerPage}
                  recordCount={recordCount}
                  setPage={setPage}
                  fetchHistory={fetchHistory}
                />
              </Grid>
            )}
          </OrderManagementPage>
        </Form>
      </FormikProvider>
      <Toast
        id="error"
        autoHideDuration={5000}
        message={errorMessage}
        open={isError}
        onClose={() => showError(false)}
        variant="error"
      />
    </>
  );
};

const useStyles = makeStyles((theme: Theme) => ({
  outlinedLabel: {
    backgroundColor: theme.palette.common.white,
    paddingLeft: 2,
    paddingRight: 2
  },
  neighborhoodWrapper: {
    padding: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    justifyContent: 'center',
    marginTop: '-2rem'
  }
}));
